import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
/** @jsxImportSource @emotion/react */
import { Select } from '@blueprintjs/select';
import { css } from '@emotion/react';
import debounce from 'lodash/debounce';
import { Filters } from 'nmr-processing';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FaRulerHorizontal, FaRulerVertical } from 'react-icons/fa';
import { MdLooksTwo } from 'react-icons/md';
import { Button, Toolbar, useSelect } from 'react-science/ui';
import { useActivePhaseTraces } from '../2d/1d-tracer/phase-correction-traces/useActivePhaseTraces';
import { useDispatch } from '../context/DispatchContext';
import ActionButtons from '../elements/ActionButtons';
import Input from '../elements/Input';
import InputRange from '../elements/InputRange';
import Label from '../elements/Label';
import { useFilter } from '../hooks/useFilter';
import useSpectrum from '../hooks/useSpectrum';
import { headerLabelStyle } from './Header';
import { HeaderContainer } from './HeaderContainer';
const defaultPhasingTypeItem = {
    label: 'Manual',
    value: 'manual',
};
const algorithms = [
    defaultPhasingTypeItem,
    {
        label: 'Automatic',
        value: 'automatic',
    },
];
const inputStyle = {
    input: {
        width: '70px',
        textAlign: 'center',
    },
    inputWrapper: {
        height: '100%',
    },
};
const emptyData = { datum: {}, filter: null };
const defaultPhaseOptions = {
    horizontal: { ph0: 0, ph1: 0 },
    vertical: { ph0: 0, ph1: 0 },
};
export default function PhaseCorrectionTwoDimensionsPanel() {
    const { activeTraceDirection, pivot, addTracesToBothDirections } = useActivePhaseTraces();
    const { data } = useSpectrum(emptyData);
    const filter = useFilter(Filters.phaseCorrectionTwoDimensions.id);
    const debounceCalculation = useRef(debounce((options) => {
        dispatch({
            type: 'CALCULATE_TOW_DIMENSIONS_MANUAL_PHASE_CORRECTION_FILTER',
            payload: { ...options, applyOn2D: true },
        });
    }, 250));
    const dispatch = useDispatch();
    const [value, setValue] = useState(defaultPhaseOptions);
    const valueRef = useRef(defaultPhaseOptions);
    const ph0Ref = useRef();
    const ph1Ref = useRef();
    const { value: phaseCorrectionTypeItem, ...defaultSelectProps } = useSelect({
        defaultSelectedItem: defaultPhasingTypeItem,
        itemTextKey: 'label',
    });
    useEffect(() => {
        if (filter && phaseCorrectionTypeItem?.value === 'manual') {
            const { value } = filter;
            const phaseOptions = defaultPhaseOptions;
            for (const direction of Object.keys(value)) {
                const { ph0, ph1 } = value[direction];
                phaseOptions[direction] = { ph0, ph1 };
            }
            setValue(phaseOptions);
            valueRef.current = phaseOptions;
        }
    }, [filter, phaseCorrectionTypeItem?.value]);
    useEffect(() => {
        if (ph0Ref.current && ph1Ref.current) {
            const { ph0, ph1 } = valueRef.current[activeTraceDirection];
            ph0Ref.current.setValue(ph0);
            ph1Ref.current.setValue(ph1);
        }
    }, [activeTraceDirection]);
    const calcPhaseCorrectionHandler = useCallback((inputValue, filedName, source) => {
        const newValue = inputValue[activeTraceDirection];
        if (filedName === 'ph1' && data && pivot) {
            const datum = data.rr;
            const nbPoints = activeTraceDirection === 'horizontal'
                ? datum.z[0].length
                : datum.z.length;
            const { ph0, ph1 } = valueRef.current[activeTraceDirection];
            const diff0 = newValue.ph0 - ph0;
            const diff1 = newValue.ph1 - ph1;
            newValue.ph0 += diff0 - (diff1 * (nbPoints - pivot?.index)) / nbPoints;
        }
        dispatch({
            type: 'CALCULATE_TOW_DIMENSIONS_MANUAL_PHASE_CORRECTION_FILTER',
            payload: { ...newValue, applyOn2D: source === 'input' },
        });
        if (source === 'inputRange') {
            debounceCalculation.current(newValue);
        }
    }, [activeTraceDirection, data, dispatch, pivot]);
    const updateInputRangeInitialValue = useCallback((value) => {
        // update InputRange initial value
        const { ph0, ph1 } = value[activeTraceDirection];
        ph0Ref.current.setValue(ph0);
        ph1Ref.current.setValue(ph1);
    }, [activeTraceDirection]);
    const handleInput = useCallback((e) => {
        const { name, value } = e.target;
        if (e.target) {
            const newValue = {
                ...valueRef.current,
                [activeTraceDirection]: {
                    ...valueRef.current[activeTraceDirection],
                    [name]: Number(value),
                },
            };
            if (String(value).trim() !== '-') {
                calcPhaseCorrectionHandler(newValue, name, 'input');
            }
            updateInputRangeInitialValue(newValue);
            valueRef.current = newValue;
            setValue(valueRef.current);
        }
    }, [
        activeTraceDirection,
        calcPhaseCorrectionHandler,
        updateInputRangeInitialValue,
    ]);
    const handleRangeChange = useCallback((e) => {
        const newValue = {
            ...valueRef.current,
            [activeTraceDirection]: {
                ...valueRef.current[activeTraceDirection],
                [e.name]: e.value,
            },
        };
        calcPhaseCorrectionHandler(newValue, e.name, 'inputRange');
        updateInputRangeInitialValue(newValue);
        valueRef.current = newValue;
        setValue(valueRef.current);
    }, [
        activeTraceDirection,
        calcPhaseCorrectionHandler,
        updateInputRangeInitialValue,
    ]);
    const handleCancelFilter = useCallback(() => {
        dispatch({
            type: 'RESET_SELECTED_TOOL',
        });
    }, [dispatch]);
    function onChangeHandler(direction) {
        dispatch({
            type: 'CHANGE_PHASE_CORRECTION_DIRECTION',
            payload: { direction },
        });
    }
    function handleApplyFilter() {
        switch (phaseCorrectionTypeItem?.value) {
            case 'automatic': {
                dispatch({
                    type: 'APPLY_AUTO_PHASE_CORRECTION_TOW_DIMENSION_FILTER',
                });
                break;
            }
            case 'manual': {
                dispatch({
                    type: 'APPLY_MANUAL_PHASE_CORRECTION_TOW_DIMENSION_FILTER',
                });
                break;
            }
            default:
                break;
        }
    }
    function handleToggleAddTraceToBothDirections() {
        dispatch({ type: 'TOGGLE_ADD_PHASE_CORRECTION_TRACE_TO_BOTH_DIRECTIONS' });
    }
    return (_jsxs(HeaderContainer, { style: { padding: '0 5px' }, children: [_jsx("div", { style: { padding: '0 5px' }, children: _jsx(Select, { items: algorithms, filterable: false, itemsEqual: "value", ...defaultSelectProps, children: _jsx(Button, { text: phaseCorrectionTypeItem?.label, rightIcon: "double-caret-vertical" }) }) }), phaseCorrectionTypeItem?.value === 'manual' && (_jsxs(_Fragment, { children: [_jsx(Label, { title: "Direction:", style: headerLabelStyle, children: _jsxs(Toolbar, { children: [_jsx(Toolbar.Item, { css: css `
                  border: 1px solid #f7f7f7;
                `, tooltip: "Horizontal", icon: _jsx(FaRulerHorizontal, {}), active: activeTraceDirection === 'horizontal', onClick: () => onChangeHandler('horizontal') }), _jsx(Toolbar.Item, { css: css `
                  border: 1px solid #f7f7f7;
                `, tooltip: "Vertical", icon: _jsx(FaRulerVertical, {}), active: activeTraceDirection === 'vertical', onClick: () => onChangeHandler('vertical') })] }) }), _jsx("div", { style: { paddingRight: '5px' }, children: _jsx(Toolbar, { children: _jsx(Toolbar.Item, { tooltip: "Add the trace in both directions", icon: _jsx(MdLooksTwo, {}), active: addTracesToBothDirections, onClick: handleToggleAddTraceToBothDirections }) }) }), _jsx(Label, { title: "PH0:", style: headerLabelStyle, children: _jsx(Input, { name: "ph0", style: inputStyle, onChange: handleInput, value: value[activeTraceDirection].ph0, type: "number", debounceTime: 250 }) }), _jsx(Label, { title: "PH1:", style: headerLabelStyle, children: _jsx(Input, { name: "ph1", style: inputStyle, onChange: handleInput, value: value[activeTraceDirection].ph1, type: "number", debounceTime: 250 }) }), _jsx(InputRange, { ref: ph0Ref, name: "ph0", label: "Change PH0 (click and drag)", shortLabel: "Ph0", style: { width: '20%' }, onChange: handleRangeChange }), _jsx(InputRange, { ref: ph1Ref, name: "ph1", label: "Change PH1 (click and drag)", shortLabel: "Ph1", style: { width: '20%' }, onChange: handleRangeChange })] })), _jsx(ActionButtons, { onDone: handleApplyFilter, onCancel: handleCancelFilter })] }));
}
